import produce from 'immer';
import * as _ from 'lodash';
import type { AnyAction } from 'redux';

import Locator from '../../../shared/Locator';
import { coerceNumber } from '../../../shared/utils/numbers';
import { errorMessageFromResponse } from '../../helpers/errors';
import { createReducer } from '../utils';
import type { ImportRepositoryResponse } from '../../../typings/api';
import type { Repository, ServiceFilter, ServiceQueryParameters, VCSInstanceData } from '../../types';

import {
  VCS_DESELECT_ALL_REPOSITORIES,
  VCS_FETCH_FILTERS,
  VCS_FETCH_INSTANCES,
  VCS_FETCH_REPOSITORIES,
  VCS_IMPORT_REPOSITORIES,
  VCS_IMPORT_REPOSITORIES_INCREMENT_GROUP_COUNT,
  VCS_RESET_IMPORT_STATUS,
  VCS_SELECT_REPOSITORY,
  VCS_SET_BRANCH_ON_REPOSITORY,
  VCS_SET_FILTER,
  VCS_SET_INSTANCE,
  VCS_SET_QUERY_PARAMETERS,
} from './types';
import type {
  VCSImportState,
  VCSInstanceState,
  VCSReducerAsyncActionData,
  VCSReducerNonAsyncActionData,
  VCSState,
} from './types';

type VCSReducerMap = Record<string, (
    state: VCSState,
    action: AnyAction, // unsure of the best type here
  ) => VCSState>;

const getInitialQueryParameters = (): ServiceQueryParameters => ({
  q: '',
  page: 1,
  pageSize: 10,
  filterType: '',
  filterValue: '',
  instanceName: '',
});

const getInitialImportStatus = (): VCSImportState => ({
  loading: false,
  isBulkImport: false,
  importGroupCount: 0,
  importGroupsCompleted: 0,
  totalRepoCount: 0,
  successfullyImportedRepoGroups: [],
});

const getInitialInstanceData = (): VCSInstanceState => ({
  connected: false,
  privateAccess: false, // has the user been granted "private" access (github specific)
  filters: [],
  repositories: [],
  selectedRepositories: [], // list of indexes from repositories
});

export const initialState: VCSState = {
  status: {
    loading: false,
    error: null,
  },
  importStatus: getInitialImportStatus(),
  query: getInitialQueryParameters(),
  instances: [],
  instanceData: {},
};

// init (if needed) and return instanceData for the given service
const getInstanceDataForService = (draftState: VCSState): VCSInstanceState => {
  const currInstance = draftState.query.instanceName;
  draftState.instanceData[currInstance] = (draftState.instanceData[currInstance] ||
    getInitialInstanceData());
  return draftState.instanceData[currInstance];
};

const actionsMap: VCSReducerMap = {
  [VCS_SET_QUERY_PARAMETERS]: (state, action: VCSReducerNonAsyncActionData<{ query: ServiceQueryParameters }>) =>
    produce(state, (draftState) => {
      draftState.query = action.payload.query;

      return draftState;
    }),
  [VCS_SET_INSTANCE]: (state, action: VCSReducerNonAsyncActionData<{ instanceName: string }>) =>
    produce(state, (draftState) => {
      draftState.query = getInitialQueryParameters();
      draftState.query.instanceName = action.payload.instanceName;

      const currentInstance = getInstanceDataForService(draftState);
      currentInstance.selectedRepositories = [];

      return draftState;
    }),
  [VCS_SET_FILTER]: (state, action: VCSReducerNonAsyncActionData<{ filterType?: string; filterValue?: string }>) =>
    produce(state, (draftState) => {
      draftState.query = getInitialQueryParameters();
      // only set instanceName if there are multiple instances
      if (draftState.instances.length > 1) {
        draftState.query.instanceName = state.query.instanceName;
      }
      draftState.query.filterType = action.payload.filterType || '';
      draftState.query.filterValue = action.payload.filterValue || '';

      const currentInstance = getInstanceDataForService(draftState);
      currentInstance.selectedRepositories = [];

      return draftState;
    }),
  [VCS_SET_BRANCH_ON_REPOSITORY]: (
    state,
    action: VCSReducerNonAsyncActionData<{ repo: Repository; branch: string }>,
  ) =>
    produce(state, (draftState) => {
      const { repo, branch } = action.payload;
      const currentInstance = getInstanceDataForService(draftState);
      const repoToUpdate = currentInstance.repositories.find(r => r.title === repo.title);
      // if repo/branch don't exist, exit
      if (!repoToUpdate || !repoToUpdate.branches?.find(b => b === branch)) {
        return draftState;
      }

      // update the locator with the new branch
      const oldLoc = new Locator(repoToUpdate.locator);
      const newLoc = new Locator({
        fetcher: oldLoc.fetcher.value,
        package: oldLoc.package.value,
        revision: branch,
      }).toFullString();

      repoToUpdate.locator = newLoc;
      repoToUpdate.branch = branch;

      // since the selectedRepositories field is a shallow clone of repositories, we
      // need to update any selectedRepositories
      const selectedRepo = currentInstance.selectedRepositories.find(r => r.title === repo.title);
      if (selectedRepo) {
        selectedRepo.locator = newLoc;
        selectedRepo.branch = branch;
      }

      return draftState;
    }),
  [VCS_SELECT_REPOSITORY]: (
    state,
    action: VCSReducerNonAsyncActionData<{ repo: Repository }>,
  ) =>
    produce(state, (draftState) => {
      const { repo } = action.payload;
      const currentInstance = getInstanceDataForService(draftState);
      const repoIndex = currentInstance.selectedRepositories.findIndex(r => r.locator === repo.locator);
      if (repoIndex !== -1) {
        currentInstance.selectedRepositories.splice(repoIndex, 1);
      } else {
        const repoToSelect = currentInstance.repositories.find(r => r.title === repo.title);
        currentInstance.selectedRepositories.push({ ...repoToSelect });
      }

      return draftState;
    }),
  [VCS_DESELECT_ALL_REPOSITORIES]: (state) => produce(state, (draftState) => {
    const currentInstance = getInstanceDataForService(draftState);
    currentInstance.selectedRepositories = [];
    return draftState;
  }),
  [`${VCS_FETCH_INSTANCES}_PENDING`]: (state) =>
    produce(state, (draftState) => {
      draftState.status.loading = true;
      draftState.status.error = null;

      return draftState;
    }),
  [`${VCS_FETCH_INSTANCES}_FULFILLED`]: (state, action: VCSReducerAsyncActionData<VCSInstanceData[]>) =>
    produce(state, (draftState) => {
      draftState.status.loading = false;
      draftState.status.error = null;

      if (!_.isEmpty(action.meta.defaultQueryParams)) {
        draftState.query = {
          ...draftState.query,
          ...action.meta.defaultQueryParams,
        };
      }

      const instances = action.payload.data;
      const instanceNames = instances.map(i => i.name);

      // pick an instance to default to (may be provided)
      let selectedInstance;
      if (action.meta.defaultQueryParams?.instanceName) {
        selectedInstance = action.meta.defaultQueryParams.instanceName;
      } else {
        selectedInstance = instances.find(i => i.connected)?.name ||
          instances[0]?.name || '';
      }

      // only set instanceName if there are multiple
      if (instances.length > 1) {
        draftState.query.instanceName = selectedInstance;
      }

      draftState.instances = instanceNames;

      return draftState;
    }),
  [`${VCS_FETCH_INSTANCES}_REJECTED`]: (state, action) =>
    produce(state, (draftState) => {
      draftState.instances = [];
      draftState.status.loading = false;
      draftState.status.error = errorMessageFromResponse(action.payload);

      return draftState;
    }),
  [`${VCS_FETCH_FILTERS}_PENDING`]: (state, action) =>
    produce(state, (draftState) => {
      if (!action.meta.isRefetch) {
        draftState.status.loading = true;
      }
      draftState.status.error = null;

      return draftState;
    }),
  [`${VCS_FETCH_FILTERS}_FULFILLED`]: (state, action: VCSReducerAsyncActionData<ServiceFilter[]>) =>
    produce(state, (draftState) => {
      draftState.status.loading = false;
      draftState.status.error = null;

      const currInstanceState = getInstanceDataForService(draftState);
      const filters = action.payload.data;
      const previousLatestInstallation = currInstanceState.filters[0]?.filters[0]?.value;

      currInstanceState.connected = true;
      currInstanceState.filters = filters;

      // set filter to first val if no filterValue, or filterValue isn't in filters
      const allFilters = filters.map((f) => f.filters);
      if (allFilters.length && filters[0]) {
        // set privateAccess for github
        const privateAccess = filters[0].private_access;
        currInstanceState.privateAccess = _.isBoolean(privateAccess) && privateAccess;

        const allFilterValues = _.flatMapDeep(allFilters, (f) => f.map((fi) => fi.value));

        // set query parameters for filterType & filterValue
        const latestInstallation = filters[0]?.filters?.[0]?.value;
        // important to check for undefined as the value could be empty due to initial page load
        const isLatestInstallationChanged =
          previousLatestInstallation && previousLatestInstallation !== latestInstallation;
        if (
          !draftState.query.filterValue ||
          !allFilterValues.includes(draftState.query.filterValue) ||
          isLatestInstallationChanged
        ) {
          draftState.query.filterType = filters[0].key;
          draftState.query.filterValue = latestInstallation || '';
        }
      }

      return draftState;
    }),
  [`${VCS_FETCH_FILTERS}_REJECTED`]: (state, action) =>
    produce(state, (draftState) => {
      draftState.status.loading = false;
      const currInstanceState = getInstanceDataForService(draftState);

      // don't set error if user isn't authed
      if (action.payload.status === 403) {
        currInstanceState.connected = false;
      } else {
        currInstanceState.connected = true;
        draftState.status.error = errorMessageFromResponse(action.payload);
      }

      currInstanceState.filters = [];

      return draftState;
    }),
  [`${VCS_FETCH_REPOSITORIES}_PENDING`]: (state, action) =>
    produce(state, (draftState) => {
      if (!action.meta.isRefetch) {
        draftState.status.loading = true;
      }
      draftState.status.error = null;
      return draftState;
    }),
  [`${VCS_FETCH_REPOSITORIES}_FULFILLED`]: (state, action: VCSReducerAsyncActionData<Repository[]>) =>
    produce(state, (draftState) => {
      draftState.status.loading = false;
      draftState.status.error = null;

      const currInstanceState = getInstanceDataForService(draftState);

      currInstanceState.connected = true;
      currInstanceState.repositories = action.payload.data;

      const totalCount = action.payload.headers.get('x-total-count') ?? undefined;
      currInstanceState.totalCount = coerceNumber(totalCount);

      return draftState;
    }),
  [`${VCS_FETCH_REPOSITORIES}_REJECTED`]: (state, action) =>
    produce(state, (draftState) => {
      draftState.status.loading = false;
      draftState.status.error = errorMessageFromResponse(action.payload);

      const currInstanceState = getInstanceDataForService(draftState);

      // don't set error if user isn't authed
      if (action.payload.status === 403) {
        currInstanceState.connected = false;
      } else {
        currInstanceState.connected = true;
        draftState.status.error = errorMessageFromResponse(action.payload);
      }

      currInstanceState.repositories = [];
      currInstanceState.totalCount = undefined;

      return draftState;
    }),
  [`${VCS_IMPORT_REPOSITORIES}_PENDING`]: (state, action: VCSReducerNonAsyncActionData<{ isBulkImport: boolean; importGroupCount: number; totalRepoCount: number }>) =>
    produce(state, (draftState) => {
      draftState.importStatus.loading = true;
      draftState.importStatus.isBulkImport = action.payload.isBulkImport;
      draftState.importStatus.importGroupCount = action.payload.importGroupCount;
      draftState.importStatus.totalRepoCount = action.payload.totalRepoCount;
      draftState.importStatus.importGroupsCompleted = 0;
      return draftState;
    }),
  [VCS_IMPORT_REPOSITORIES_INCREMENT_GROUP_COUNT]: (state) =>
    produce(state, (draftState) => {
      draftState.importStatus.importGroupsCompleted++;

      return draftState;
    }),
  /**
   * This reducer receives a list of response(s) from the
   * '/api/services/:service/import' endpoint for each repo group.
   * If a given group has more than the configured bulk threshold, then
   * the reponse will be of type { queued: number }, indicating that the repos
   * will be imported asyncronously in a background task. If it has less than the
   * bulk threshold, then the response will be of type { imported: number }, indicating
   * that the repos have been imported syncronously.
   * */
  [`${VCS_IMPORT_REPOSITORIES}_FULFILLED`]: (state, action: VCSReducerNonAsyncActionData<{ importedRepos: ImportRepositoryResponse[] }>) =>
    produce(state, (draftState) => {
      draftState.status.error = null;
      draftState.importStatus.loading = false;
      draftState.importStatus.successfullyImportedRepoGroups = action.payload.importedRepos;

      return draftState;
    }),
  [`${VCS_IMPORT_REPOSITORIES}_REJECTED`]: (state, action) =>
    produce(state, (draftState) => {
      draftState.importStatus.loading = false;
      draftState.importStatus.importGroupsCompleted = 0;
      draftState.importStatus.totalRepoCount = 0;
      draftState.status.error = errorMessageFromResponse(action.payload);

      return draftState;
    }),
  [VCS_RESET_IMPORT_STATUS]: (state) =>
    produce(state, (draftState) => {
      draftState.importStatus = getInitialImportStatus();
      draftState.query = getInitialQueryParameters();
      draftState.instances = [];
      draftState.instanceData = {};

      return draftState;
    }),
};

// @ts-ignore: suspicious type
export default createReducer(initialState, actionsMap);
